import { ApolloLink, Observable } from '@apollo/client/core';
import { getPiezoClient } from '../provisioning/PiezoClient';
import { getTumblerConfig } from '../../../utils/appConfig';
import { PiezoDebugPrefix, PIEZO_REFRESH_HINT } from '../provisioning/PiezoClient.constants';
import { createPiezoArtifactMap, PiezoArtifactMap, PiezoToolCall } from './toolCalls';
import { deriveToolCallsFromOperation } from './fieldRegistry';
import { isPiezoEnabledEnvironment } from './environment';
import { isToolPiezoRequired } from './migratedTools';

/**
 * Single browser-side trigger for Piezo artifact creation. An operation declares its tool calls via Apollo
 * context `piezoToolCalls`; the link signs each and injects a `piezoArtifacts` map into the variables, so the
 * server runs those exact parameters.
 *
 * An operation that signs first awaits the shared init. init() is idempotent, so this joins the run
 * index.tsx started on operator entry rather than racing it -- signed-ness no longer depends on timing.
 * Operations without tool calls pass straight through.
 *
 * Degrades per field, not per operation: an operation is sent even when some of its tools could not be
 * signed, so the fields that were signed still resolve.
 */
export const piezoLink = new ApolloLink((operation, forward) => {
  return new Observable((observer) => {
    let subscription: { unsubscribe: () => void } | undefined;
    let cancelled = false;

    (async () => {
      const explicit: PiezoToolCall[] | undefined = operation.getContext()?.piezoToolCalls;
      const calls: PiezoToolCall[] = explicit?.length
        ? explicit
        : deriveToolCallsFromOperation(operation.query, operation.variables);

      const mustSign = isPiezoEnabledEnvironment()
        ? calls.filter((call) => isToolPiezoRequired(call.toolId))
        : [];

      let initFailure: string | undefined;
      let unsigned: PiezoToolCall[] = [];

      if (mustSign.length > 0) {
        const region = getTumblerConfig()?.current?.publicRegion;
        if (!region) {
          initFailure = 'no publicRegion in the app config, so Piezo init was never started';
        } else {
          const initResult = await getPiezoClient().init({ region });
          if ('error' in initResult && initResult.error) {
            initFailure = initResult.error.message;
          }
        }

        let piezoArtifacts: PiezoArtifactMap = {};
        // init() drops its memo on failure, so signing here would start a second provision.
        if (!initFailure) {
          try {
            piezoArtifacts = await createPiezoArtifactMap(mustSign);
          } catch (err) {
            console.warn(
              `${PiezoDebugPrefix} piezoLink: artifact creation failed for ${operation.operationName}:`,
              err,
            );
          }
        }
        operation.variables = { ...operation.variables, piezoArtifacts };
        unsigned = mustSign.filter((call) => !piezoArtifacts[call.key]);
      }

      if (cancelled) {
        return;
      }

      if (unsigned.length > 0) {
        // Sent anyway: invokeTool rejects an unsigned Piezo-required tool without calling the proxy, and
        // no resolver field is non-null, so only those fields come back null.
        const tools = unsigned.map((call) => call.toolId).join(', ');
        console.warn(
          `${PiezoDebugPrefix} piezoLink: ${operation.operationName} sending with ` +
            `${unsigned.length} unsigned Piezo-required tool(s) [${tools}]; those fields will error. ` +
            `${initFailure ? `Piezo startup failed: ${initFailure}` : 'Piezo startup completed.'}`,
        );
      }

      subscription = forward(operation).subscribe({
        next: (value) => observer.next(value),
        error: (err) => observer.error(err),
        complete: () => observer.complete(),
      });
    })().catch((err) => {
      if (cancelled) {
        return;
      }
      console.error(
        `${PiezoDebugPrefix} piezoLink: unexpected failure for ${operation.operationName}:`,
        err,
      );
      observer.error(
        new Error(
          `Piezo could not prepare this request (${String(err)}), so it was not sent. ${PIEZO_REFRESH_HINT}`,
        ),
      );
    });

    return () => {
      cancelled = true;
      subscription?.unsubscribe();
    };
  });
});
